import { useState } from 'react';
import { CheckCircle2, ChevronDown, Dumbbell, Lightbulb, Quote, RotateCcw, XCircle, ArrowLeft } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { RuleResults } from './RuleResults';
import { formatDateTime } from './eeocUtils';
import { QualityTaskOutcome } from '../quality/QualityTaskOutcome';

const ReportHero = ({ session }) => (
    <div className={`relative overflow-hidden rounded-2xl border p-6 ${session.passed ? 'border-emerald-300 bg-emerald-500/5' : 'border-red-300 bg-red-500/5'}`} data-testid="eeoc-report-hero">
        <div className={`absolute inset-x-0 top-0 h-1.5 ${session.passed ? 'bg-emerald-500' : 'bg-red-500'}`} />
        <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Session report · {session.module.title}</p>
                <div className="mt-2 flex items-center gap-3">
                    {session.passed ? <CheckCircle2 className="h-8 w-8 text-emerald-600" /> : <XCircle className="h-8 w-8 text-red-600" />}
                    <span className={`text-3xl font-bold ${session.passed ? 'text-emerald-700' : 'text-red-700'}`} data-testid="eeoc-report-result">
                        {session.passed ? 'PASS' : 'FAIL'}
                    </span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                    Minimum passing score: <span className="font-medium text-foreground" data-testid="eeoc-report-passing-score">{session.passing_score}</span>
                    {' · '}{session.rules_passed}/{session.rules_total} rule checks passed · {formatDateTime(session.completed_at)}
                </p>
            </div>
            <div className="text-right">
                <p className="text-6xl font-bold tabular-nums" data-testid="eeoc-report-score">{session.overall_score}</p>
                <p className="text-xs text-muted-foreground">overall compliance score</p>
            </div>
        </div>
    </div>
);

const ScenarioBreakdown = ({ sc, index }) => {
    const [open, setOpen] = useState(false);
    return (
        <Card data-testid={`eeoc-report-scenario-${index}`}>
            <button className="flex w-full items-center justify-between gap-3 p-4 text-left" onClick={() => setOpen(!open)} data-testid={`eeoc-report-scenario-toggle-${index}`}>
                <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Scenario {index + 1} · {sc.scenario.setting}</p>
                    <p className="font-medium">{sc.scenario.title}</p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xl font-semibold tabular-nums">{sc.score}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                </div>
            </button>
            {open && (
                <CardContent className="space-y-4 border-t pt-4">
                    <RuleResults rules={sc.evaluation.rules} testPrefix={`eeoc-report-rule-${index}`} />
                    <div className="space-y-1.5 rounded-xl bg-muted/40 p-3 text-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Transcript</p>
                        {sc.turns.map((t, i) => (
                            <p key={i}><span className="font-medium">{t.role === 'recruiter' ? 'You' : 'Candidate'}:</span> {t.text}</p>
                        ))}
                    </div>
                </CardContent>
            )}
        </Card>
    );
};

const DidRight = ({ items }) => (
    <section className="space-y-3" data-testid="eeoc-report-did-right">
        <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> What you did right</h2>
        {items.length === 0 && <p className="text-sm text-muted-foreground">No compliant moments were highlighted this time - review the fixes below.</p>}
        {items.map((d, i) => (
            <div key={i} className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4">
                <p className="flex gap-2 text-sm italic"><Quote className="h-4 w-4 shrink-0 text-emerald-600" />"{d.quote}"</p>
                <p className="mt-1.5 text-sm text-muted-foreground">{d.why}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">{d.scenario_title}</p>
            </div>
        ))}
    </section>
);

const Mistakes = ({ items }) => (
    <section className="space-y-3" data-testid="eeoc-report-mistakes">
        <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold"><XCircle className="h-5 w-5 text-red-600" /> What to fix</h2>
        {items.length === 0 && <p className="text-sm text-muted-foreground">No violations - excellent, fully compliant conversations.</p>}
        {items.map((m, i) => (
            <Card key={i} className="border-red-500/25" data-testid={`eeoc-mistake-${i}`}>
                <CardContent className="space-y-3 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="border-red-300 text-red-700">{m.rule_title}</Badge>
                        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{m.scenario_title}</span>
                    </div>
                    {m.quote && <p className="text-sm italic text-red-800 line-through decoration-red-400/60 dark:text-red-300">"{m.quote}"</p>}
                    <p className="text-sm">{m.issue}</p>
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700"><Lightbulb className="h-3.5 w-3.5" /> Try this instead</p>
                        <p className="mt-1 text-sm" data-testid={`eeoc-try-instead-${i}`}>"{m.try_instead}"</p>
                    </div>
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700"><Dumbbell className="h-3.5 w-3.5" /> Practice drill</p>
                        <p className="mt-1 text-sm" data-testid={`eeoc-drill-${i}`}>{m.drill}</p>
                    </div>
                </CardContent>
            </Card>
        ))}
    </section>
);

export const SessionReport = ({ session, onRetake, onBack, retaking }) => (
    <div className="space-y-8" data-testid="eeoc-session-report">
        <ReportHero session={session} />
        {!session.read_only && (
            <QualityTaskOutcome kind="eeoc" refKey={session.module_id} attemptScore={session.overall_score} onRetake={onRetake} retaking={retaking}>
                <div className="flex flex-wrap gap-3">
                    <Button onClick={onRetake} disabled={retaking} className={session.passed ? '' : 'bg-sky-600 hover:bg-sky-700'} variant={session.passed ? 'outline' : 'default'} data-testid="eeoc-retake-btn">
                        <RotateCcw className="mr-2 h-4 w-4" /> {session.passed ? 'Practice this module again' : 'Retake scenarios'}
                    </Button>
                    <Button variant="ghost" onClick={onBack} data-testid="eeoc-report-back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Back to EEOC Training</Button>
                </div>
            </QualityTaskOutcome>
        )}
        <section className="space-y-3">
            <h2 className="text-base md:text-lg font-semibold">Rule-by-rule results</h2>
            {session.scenarios.map((sc, i) => <ScenarioBreakdown key={sc.scenario_id} sc={sc} index={i} />)}
        </section>
        <div className="grid gap-8 lg:grid-cols-2">
            <DidRight items={session.coaching?.did_right || []} />
            <Mistakes items={session.coaching?.mistakes || []} />
        </div>
    </div>
);
